import React, { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, AlertCircle, User, Settings, TrendingUp, Package, MessageSquare, DollarSign, Plus, RefreshCw, Download, BarChart3, Building, ShoppingBag, FileText, HelpCircle } from 'lucide-react';
import { SupplierBrandManagement } from '@/polymet/components/supplier-brand-management';
import { useSupplierDashboard } from '@/polymet/hooks/use-supplier-dashboard';
import { DashboardTab } from '@/polymet/types/supplier';
import StatsOverview from '@/polymet/components/dashboard/stats-overview';
import ProductsTable from '@/polymet/components/dashboard/products-table';
import { SupplierBrandManagementImproved } from '@/polymet/components/supplier-brand-management-improved';
import { SupplierProfileForm } from '@/polymet/components/supplier-profile-form';
import SupplierRFQDashboard from '@/polymet/pages/supplier-rfq-dashboard';
import InquiryManagementPage from '@/polymet/pages/inquiry-management-page';

// Notification types for user feedback
type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface NotificationProps {
  type: NotificationType;
  message: string;
  onDismiss?: () => void;
}

const Notification = memo<NotificationProps>(({ type, message, onDismiss }) => {
  const icons = {
    success: <CheckCircle className="h-5 w-5" />,
    error: <AlertCircle className="h-5 w-5" />,
    warning: <AlertCircle className="h-5 w-5" />,
    info: <AlertCircle className="h-5 w-5" />
  };

  const variants = {
    success: 'bg-green-50 border-green-200 text-green-700',
    error: 'bg-red-50 border-red-200 text-red-700',
    warning: 'bg-amber-50 border-amber-200 text-amber-700',
    info: 'bg-blue-50 border-blue-200 text-blue-700'
  };

  return (
    <Alert className={`flex items-center ${variants[type]}`}>
      {icons[type]}
      <AlertDescription className="ml-2">{message}</AlertDescription>
    </Alert>
  );
});

Notification.displayName = 'Notification';

// Tab navigation component
interface TabNavigationProps {
  activeTab: DashboardTab;
  onTabChange: (tab: DashboardTab) => void;
}

const TabNavigation = memo<TabNavigationProps>(({ activeTab, onTabChange }) => {
  const tabs: { id: DashboardTab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <User className="h-4 w-4" /> },
    { id: 'profile', label: 'Profile', icon: <Settings className="h-4 w-4" /> },
    { id: 'brands', label: 'Brands', icon: <User className="h-4 w-4" /> },
    { id: 'products', label: 'Products', icon: <User className="h-4 w-4" /> },
    { id: 'rfqs', label: 'RFQs', icon: <FileText className="h-4 w-4" /> },
    { id: 'inquiries', label: 'Inquiries', icon: <HelpCircle className="h-4 w-4" /> }
  ];

  return (
    <nav className="border-b border-border" role="tablist" aria-label="Dashboard sections">
      <div className="flex space-x-8 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            onClick={() => onTabChange(tab.id)}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>
    </nav>
  );
});

TabNavigation.displayName = 'TabNavigation';

// Main dashboard header component
interface DashboardHeaderProps {
  supplier: any;
  onAddProduct: () => void;
  onEditProfile: () => void;
}

const DashboardHeader = memo<DashboardHeaderProps>(({ supplier, onAddProduct, onEditProfile }) => (
  <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
    <div className="flex items-center gap-4">
      <Avatar className="h-16 w-16 border-2 border-primary">
        <AvatarImage src={supplier?.avatar} alt={`${supplier?.name} avatar`} />
        <AvatarFallback>{supplier?.name?.charAt(0) || 'S'}</AvatarFallback>
      </Avatar>
      <div>
        <h1 className="text-2xl font-bold">{supplier?.name || 'Supplier Dashboard'}</h1>
        <p className="text-muted-foreground">{supplier?.company || 'Industrial Equipment Supplier'}</p>
        {supplier?.location && (
          <p className="text-sm text-muted-foreground">{supplier.location}</p>
        )}
      </div>
    </div>
    <div className="flex items-center gap-2">
      <Button onClick={onAddProduct} className="bg-primary hover:bg-primary/90">
        Add Product
      </Button>
      <Button variant="outline" size="sm" onClick={onEditProfile}>
        Edit Profile
      </Button>
    </div>
  </div>
));

DashboardHeader.displayName = 'DashboardHeader';

// Loading component
const DashboardLoading = memo(() => (
  <div className="container mx-auto px-4 py-8">
    <div className="flex flex-col items-center justify-center h-[60vh]">
      <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
      <p className="text-muted-foreground">Loading dashboard data...</p>
    </div>
  </div>
));

DashboardLoading.displayName = 'DashboardLoading';

// Error fallback component
interface ErrorFallbackProps {
  error: string;
  onRetry: () => void;
}

const ErrorFallback = memo<ErrorFallbackProps>(({ error, onRetry }) => (
  <div className="container mx-auto px-4 py-8">
    <div className="max-w-md mx-auto text-center">
      <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
      <h2 className="text-lg font-semibold mb-2">Failed to Load Dashboard</h2>
      <p className="text-muted-foreground mb-4">{error}</p>
      <Button onClick={onRetry}>Try Again</Button>
    </div>
  </div>
));

ErrorFallback.displayName = 'ErrorFallback';

// Main dashboard component
const SupplierDashboardImproved = memo(() => {
  const navigate = useNavigate();
  
  const {
    supplier,
    products,
    documents,
    stats,
    activeTab,
    loadingStates,
    errorStates,
    notifications,
    setActiveTab,
    loadDashboardData,
    deleteProduct,
    deleteDocument,
    uploadDocument,
    refreshProducts,
    clearErrors,
    addNotification,
    removeNotification,
    clearAllNotifications
  } = useSupplierDashboard();

  // Navigation handlers
  const handleAddProduct = useCallback(() => {
    navigate('/supplier/products/add');
  }, [navigate]);

  const handleEditProfile = useCallback(() => {
    setActiveTab('profile');
  }, [setActiveTab]);

  // Product deletion with user feedback
  const handleDeleteProduct = useCallback(async (productId: string) => {
    try {
      await deleteProduct(productId);
      // Success feedback is handled by the hook
    } catch (error) {
      // Error feedback is handled by the hook
      console.error('Failed to delete product:', error);
    }
  }, [deleteProduct]);

  // Show loading state
  if (loadingStates.dashboard && !supplier) {
    return <DashboardLoading />;
  }

  // Show error state
  if (errorStates.dashboard && !supplier) {
    return (
      <ErrorFallback 
        error={errorStates.dashboard} 
        onRetry={loadDashboardData}
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Success Messages - from notifications */}
      {notifications.filter(n => n.type === 'success').map(notification => (
        <div key={notification.id} className="mb-6">
          <Notification
            type="success"
            message={notification.message}
            onDismiss={() => removeNotification(notification.id)}
          />
        </div>
      ))}

      {/* Error Messages */}
      {Object.entries(errorStates).map(([key, error]) => 
        error && key !== 'dashboard' ? (
          <div key={key} className="mb-6">
            <Notification
              type="error"
              message={error}
              onDismiss={() => clearErrors()}
            />
          </div>
        ) : null
      )}

      {/* Error notifications */}
      {notifications.filter(n => n.type === 'error').map(notification => (
        <div key={notification.id} className="mb-6">
          <Notification
            type="error"
            message={notification.message}
            onDismiss={() => removeNotification(notification.id)}
          />
        </div>
      ))}

      {/* Dashboard Header */}
      <DashboardHeader
        supplier={supplier}
        onAddProduct={handleAddProduct}
        onEditProfile={handleEditProfile}
      />

      {/* Tab Navigation */}
      <div className="mb-6">
        <TabNavigation
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* Overview Tab */}
        <div
          role="tabpanel"
          id="panel-overview"
          aria-labelledby="tab-overview"
          style={{ display: activeTab === 'overview' ? 'block' : 'none' }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Dashboard Overview</CardTitle>
              <CardDescription>View your supplier performance at a glance</CardDescription>
            </CardHeader>
            <CardContent>
              <StatsOverview
                stats={stats}
                loading={loadingStates.dashboard}
              />
            </CardContent>
          </Card>
        </div>

        {/* Profile Tab */}
        <div
          role="tabpanel"
          id="panel-profile"
          aria-labelledby="tab-profile"
          style={{ display: activeTab === 'profile' ? 'block' : 'none' }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Supplier Profile</CardTitle>
              <CardDescription>Update your business information and profile details</CardDescription>
            </CardHeader>
            <CardContent>
              <SupplierProfileForm supplierId={supplier?.id || ''} />
            </CardContent>
          </Card>
        </div>

        {/* Brands Tab */}
        <div
          role="tabpanel"
          id="panel-brands"
          aria-labelledby="tab-brands"
          style={{ display: activeTab === 'brands' ? 'block' : 'none' }}
        >
          <SupplierBrandManagement supplierId={supplier?.id || ''} />
        </div>

        {/* Products Tab */}
        <div
          role="tabpanel"
          id="panel-products"
          aria-labelledby="tab-products"
          style={{ display: activeTab === 'products' ? 'block' : 'none' }}
        >
          <ProductsTable
            products={[...products]}
            loading={loadingStates.products || loadingStates.delete}
            onAddProduct={handleAddProduct}
            onDeleteProduct={handleDeleteProduct}
            onRefresh={refreshProducts}
          />
        </div>

        {/* RFQ Tab */}
        <div
          role="tabpanel"
          id="panel-rfqs"
          aria-labelledby="tab-rfqs"
          style={{ display: activeTab === 'rfqs' ? 'block' : 'none' }}
        >
          <Card>
            <CardHeader>
              <CardTitle>RFQ Management</CardTitle>
              <CardDescription>Manage quote requests and respond to buyer inquiries</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="[&>div]:!px-0 [&>div]:!py-0 [&>div]:!container-none">
                <SupplierRFQDashboard />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Inquiry Tab */}
        <div
          role="tabpanel"
          id="panel-inquiries"
          aria-labelledby="tab-inquiries"
          style={{ display: activeTab === 'inquiries' ? 'block' : 'none' }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Inquiry Management</CardTitle>
              <CardDescription>View and respond to customer inquiries and convert them to RFQs</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="[&>div]:!px-0 [&>div]:!py-0 [&>div]:!container-none">
                <InquiryManagementPage />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
});

SupplierDashboardImproved.displayName = 'SupplierDashboardImproved';

export default SupplierDashboardImproved; 